// RRULE da e verso una forma compilabile, più la frase in italiano.
// Il backend accetta FREQ, INTERVAL, BYDAY, BYMONTHDAY, BYSETPOS, COUNT, UNTIL.

export const GIORNI = [
  { id: 'MO', breve: 'Lun', nome: 'lunedì' },
  { id: 'TU', breve: 'Mar', nome: 'martedì' },
  { id: 'WE', breve: 'Mer', nome: 'mercoledì' },
  { id: 'TH', breve: 'Gio', nome: 'giovedì' },
  { id: 'FR', breve: 'Ven', nome: 'venerdì' },
  { id: 'SA', breve: 'Sab', nome: 'sabato' },
  { id: 'SU', breve: 'Dom', nome: 'domenica' },
];

export const POSIZIONI = [
  { id: 1, nome: 'il primo' }, { id: 2, nome: 'il secondo' }, { id: 3, nome: 'il terzo' },
  { id: 4, nome: 'il quarto' }, { id: -1, nome: "l'ultimo" },
];

const UNITA = {
  DAILY: ['giorno', 'giorni'],
  WEEKLY: ['settimana', 'settimane'],
  MONTHLY: ['mese', 'mesi'],
  YEARLY: ['anno', 'anni'],
};

export const vuota = () => ({
  freq: '', interval: 1, byday: [], mensile: 'giorno',
  bymonthday: null, bysetpos: 1, bydayPos: 'MO', fine: 'mai', until: '', count: 10,
});

/** Da RRULE a forma compilabile. */
export function daRrule(rrule) {
  const stato = vuota();
  if (!rrule) return stato;
  const parti = Object.fromEntries(
    rrule.replace(/^RRULE:/i, '').split(';').filter(Boolean)
      .map(p => { const [k, v] = p.split('='); return [k.toUpperCase(), v]; }));

  stato.freq = parti.FREQ || '';
  stato.interval = Number(parti.INTERVAL || 1);

  if (parti.BYSETPOS && parti.BYDAY) {
    stato.mensile = 'posizione';
    stato.bysetpos = Number(parti.BYSETPOS);
    stato.bydayPos = parti.BYDAY.split(',')[0];
  } else if (parti.BYDAY) {
    stato.byday = parti.BYDAY.split(',');
  }
  if (parti.BYMONTHDAY) {
    stato.mensile = 'giorno';
    stato.bymonthday = Number(parti.BYMONTHDAY);
  }
  if (parti.COUNT) { stato.fine = 'dopo'; stato.count = Number(parti.COUNT); }
  else if (parti.UNTIL) {
    stato.fine = 'il';
    const u = parti.UNTIL;
    stato.until = `${u.slice(0, 4)}-${u.slice(4, 6)}-${u.slice(6, 8)}`;
  }
  return stato;
}

/** Dalla forma compilabile a RRULE. Stringa vuota = non si ripete. */
export function aRrule(stato, inizio) {
  if (!stato.freq) return '';
  const parti = [`FREQ=${stato.freq}`];
  if (stato.interval > 1) parti.push(`INTERVAL=${stato.interval}`);

  if (stato.freq === 'WEEKLY' && stato.byday.length) {
    parti.push(`BYDAY=${stato.byday.join(',')}`);
  }
  if (stato.freq === 'MONTHLY') {
    if (stato.mensile === 'posizione') {
      parti.push(`BYDAY=${stato.bydayPos}`, `BYSETPOS=${stato.bysetpos}`);
    } else if (stato.bymonthday) {
      parti.push(`BYMONTHDAY=${stato.bymonthday}`);
    } else if (inizio) {
      parti.push(`BYMONTHDAY=${new Date(inizio).getDate()}`);
    }
  }
  if (stato.fine === 'dopo') parti.push(`COUNT=${Math.max(1, Number(stato.count) || 1)}`);
  if (stato.fine === 'il' && stato.until) {
    parti.push(`UNTIL=${stato.until.replaceAll('-', '')}T235959`);
  }
  return parti.join(';');
}

const elenco = (voci) => voci.length <= 1
  ? (voci[0] || '')
  : `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`;

/** La frase che spiega la regola, per non far indovinare l'utente. */
export function descrivi(stato, inizio) {
  if (!stato.freq) return 'Non si ripete.';
  const n = Math.max(1, Number(stato.interval) || 1);
  const [singolare, plurale] = UNITA[stato.freq] || ['volta', 'volte'];
  let frase = n === 1 ? `Ogni ${singolare}` : `Ogni ${n} ${plurale}`;

  if (stato.freq === 'WEEKLY' && stato.byday.length) {
    const nomi = GIORNI.filter(g => stato.byday.includes(g.id)).map(g => g.nome);
    frase += ` il ${elenco(nomi)}`;
  }
  if (stato.freq === 'MONTHLY') {
    if (stato.mensile === 'posizione') {
      const pos = POSIZIONI.find(p => p.id === Number(stato.bysetpos));
      const giorno = GIORNI.find(g => g.id === stato.bydayPos);
      frase += ` ${pos?.nome || 'il primo'} ${giorno?.nome || 'lunedì'}`;
    } else {
      const giorno = stato.bymonthday || (inizio ? new Date(inizio).getDate() : null);
      if (giorno) frase += ` il giorno ${giorno}`;
    }
  }
  if (stato.fine === 'dopo') {
    const volte = Math.max(1, Number(stato.count) || 1);
    frase += `, per ${volte} ${volte === 1 ? 'volta' : 'volte'}`;
  }
  if (stato.fine === 'il' && stato.until) {
    frase += `, fino al ${new Date(stato.until).toLocaleDateString('it-IT')}`;
  }
  return `${frase}.`;
}

/** Preset per i casi frequenti, calcolati sulla data di inizio scelta. */
export function preset(inizio) {
  const data = inizio ? new Date(inizio) : new Date();
  const giorno = GIORNI[(data.getDay() + 6) % 7];
  return [
    { id: '', etichetta: 'Non si ripete' },
    { id: 'DAILY', etichetta: 'Ogni giorno', stato: { ...vuota(), freq: 'DAILY' } },
    { id: 'WEEKLY', etichetta: `Ogni settimana il ${giorno.nome}`, stato: { ...vuota(), freq: 'WEEKLY', byday: [giorno.id] } },
    { id: 'MONTHLY', etichetta: `Ogni mese il giorno ${data.getDate()}`, stato: { ...vuota(), freq: 'MONTHLY', mensile: 'giorno', bymonthday: data.getDate() } },
    { id: 'YEARLY', etichetta: 'Ogni anno', stato: { ...vuota(), freq: 'YEARLY' } },
    { id: 'custom', etichetta: 'Personalizzata…' },
  ];
}
